import { differenceInMinutes } from 'date-fns';
import i18n from '../../i18n';
import { client } from '../../gql/client';
import { resolveImageUrl } from '../cloudinary';
import { isSketchName, splitSketchImages } from './reportImages';
import { createPhotoLocationContext, photoLocationLines } from '../photoLocation';
import { GET_ORDER_BY_ID } from '../../gql/queries/orders/orders';
import { GET_ACTION_BY_ID } from '../../gql/queries/actions/actions';
import {
  GET_ACCIDENT_BY_ID,
  GET_WEATHER_BY_ID,
  GET_LUMINOSITY_BY_ID,
  GET_SURFACE_CONDITION_BY_ID,
  GET_CONDITIONS_DRIVING_BY_ID,
  GET_IMAGE_BY_ID,
  GET_MEDIUM_KNOWLEDGE_BY_ID,
  GET_SURVEILLANCE_BODY_BY_ID,
} from '../../gql/queries/accidents/accidents';
import { GET_ATTENTION_ACCIDENT_BY_ID } from '../../gql/queries/accidents/attentionAccidents';
import {
  GET_CAPITOL_BY_ID,
  GET_SUB_CAPITOL_BY_ID,
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_PREDEFINED_ORDERS,
  GET_CLASSIFICATION_BY_ID_OR_ROID,
} from '../../gql/queries/orders/predefinedData';
import {
  GET_CONCESSION_BY_ID,
  GET_ROAD_BY_ID,
} from '../../gql/queries/geoPositions/roads';
import { GET_USER_BY_ID, FETCH_USERS } from '../../gql/queries/users';
import {
  GET_VEHICLE_BY_ID,
  GET_VEHICLE_OCCUPANT_BY_ID,
  GET_VEHICLE_CONTEXTS_BY_ACCIDENT,
} from '../../gql/queries/vehicles';
import {
  GET_MATERIAL_UNIT_BY_ID,
  GET_MATERIAL_BY_ID,
} from '../../gql/queries/actions/materials';
import { formatQuantity } from '../decimalFormat';
import {
  GET_AUX_MACHINE_UNIT_BY_ID,
  GET_AUX_MACHINE_BY_ID,
} from '../../gql/queries/actions/auxMachines';
import {
  GET_ANIMAL_UNIT_BY_ID,
  GET_ANIMAL_BY_ID,
  GET_ANIMAL_STATUS_BY_ID,
} from '../../gql/queries/actions/animals';
import { GET_ACTION_BUDGET_ITEM_BY_ID } from '../../gql/queries/budgets/actionBudgetItems';
import { GET_ACTIVITY_BY_ID } from '../../gql/queries/actions/activitys';
import { GET_ASSET_BY_ID } from '../../gql/queries/actions/assets';
import { GET_EXTERNAL_WORK_BY_ID } from '../../gql/queries/actions/externalWorks';
import { GET_ELEMENT_ORDER_BY_ID } from '../../gql/queries/orders/predefinedData';
import { GET_SELECTORS_DATA } from '../../gql/queries/orders/orders';
import { FETCH_EXTERNAL_COMPANIES } from '../../gql/queries/actions/externalCompanies';
import {
  GET_COMMUNICATION_BY_ID,
  GET_INTERLOCUTOR_GROUP_BY_ID,
  GET_MEDIA_OF_COMMUNICATION_BY_ID,
} from '../../gql/queries/communication';

import { laneLabel, trackLabel } from '../catalogLabels';
import { GET_EXTERNAL_COMPANY_BY_ID } from '../../gql/queries/actions/externalCompanies';
import { roleCanViewBudgets } from '../rolePermissions';
import { isBasicAction } from '../order';
import { decompressSessionStorage } from '../sessionStorage';
import { resolveIsLaneCut } from '../laneCut';
import { loadLaneCutIds } from '../../gql/selectors/laneCutCatalog';
import type { DetailedPDFRecord } from './detailedTypes';
import { buildVehiclesInvolved } from './vehiclesInvolved';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const CHUNK_SIZE = 5;

/**
 * Lee el rol activo del usuario desde sessionStorage. Mismo patrón que
 * `WorkOrderDetailed/loader.ts` (getCurrentRole). Si falla (sesión rota,
 * ambiente test, etc.) devuelve null.
 *
 * Necesario porque los roles ciegos a budgets (capataz) NO pueden lanzar
 * queries de partidas: el backend respondería Forbidden y rompería el PDF.
 */
function getCurrentRole(): string | null {
  try {
    const raw = sessionStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(decompressSessionStorage(raw));
    return parsed?.user?.rolActive ?? null;
  } catch {
    return null;
  }
}

/**
 * Format a PK value as "km + meter km"
 */
function formatPk(
  km: number | null | undefined,
  meter: number | null | undefined
): string {
  if (km == null || meter == null) return '';
  return `${km} + ${String(meter).padStart(3, '0')} km`;
}

/**
 * Format a ROID (can be string "A.B.C" or already an array)
 */
export function formatRoid(roid: string | string[] | undefined): string {
  if (!roid) return '';
  if (Array.isArray(roid)) return roid.join('.');
  return roid;
}

/**
 * Format a date for display in the PDF
 */
export function formatDate(d: any): string {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('ca-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(d); }
}

/** Helper: get translation using current language */
function t(key: string, defaultValue?: string): string {
  return i18n.t(key, { defaultValue: defaultValue ?? key });
}

/**
 * Translate order state using i18n (common:states.{state})
 */
export function translateState(state: string): string {
  const translated = t(`common:states.${state}`, state);
  return translated;
}

/**
 * Etiqueta de Carril y de Tipo de vía para el informe.
 *
 * Delegan en el módulo único del catálogo (`utils/catalogLabels`). Antes
 * devolvían el nombre en catalán escrito a mano, así que el PDF salía con
 * «Dret» y «Calçada» aunque el informe se pidiera en inglés — que es el
 * fallo que destapó todo esto (prueba u15 del RV).
 */
function translateLane(value: string): string {
  return laneLabel(value);
}

function translateTrack(value: string): string {
  return trackLabel(value);
}


/**
 * Safe query wrapper: returns data or null on any error.
 */
export async function safeQuery<T>(
  queryDoc: any,
  variables: Record<string, any>,
  fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'
): Promise<T | null> {
  try {
    const { data } = await client.query({
      query: queryDoc,
      variables,
      fetchPolicy,
    });
    return data as T;
  } catch {
    return null;
  }
}

/**
 * Process items in parallel chunks of CHUNK_SIZE
 */
async function chunkedParallel<TIn, TOut>(
  items: TIn[],
  fn: (item: TIn) => Promise<TOut>
): Promise<TOut[]> {
  const results: TOut[] = [];
  for (let i = 0; i < items.length; i += CHUNK_SIZE) {
    const chunk = items.slice(i, i + CHUNK_SIZE);
    const chunkResults = await Promise.all(chunk.map(fn));
    results.push(...chunkResults);
  }
  return results;
}

// ---------------------------------------------------------------------------
// Cache helpers — try to resolve names from bulk-fetched cache first
// ---------------------------------------------------------------------------

function resolveCapitolName(capitolId: string): string | null {
  try {
    const data = client.readQuery({ query: FETCH_CAPITOLS });
    const found = data?.capitols?.find((c: any) => c.id === capitolId);
    return found?.name ?? null;
  } catch {
    return null;
  }
}

function resolveSubCapitolName(subCapitolId: string): string | null {
  try {
    const data = client.readQuery({ query: FETCH_SUB_CAPITOLS });
    const found = data?.subCapitols?.find((c: any) => c.id === subCapitolId);
    return found?.name ?? null;
  } catch {
    return null;
  }
}

function resolveConcessionName(concessionId: string): string | null {
  try {
    const data = client.readQuery({ query: GET_SELECTORS_DATA });
    const found = data?.concessions?.find((c: any) => c.id === concessionId);
    return found?.name ?? null;
  } catch {
    return null;
  }
}

function resolveRoadName(roadId: string): string | null {
  try {
    const data = client.readQuery({ query: GET_SELECTORS_DATA });
    const found = data?.roads?.find((r: any) => r.id === roadId);
    return found?.name ?? null;
  } catch {
    return null;
  }
}

function resolveUserFromCache(userId: string): { name: string; surName: string; concessionIDs: string[] } | null {
  try {
    const data = client.readQuery({ query: FETCH_USERS });
    const found = data?.users?.find((u: any) => u.id === userId);
    return found ?? null;
  } catch {
    return null;
  }
}

/**
 * Resuelve el nombre del "operario que registra" (Order.registeredByUserId) a
 * "Nombre Apellido". Cache-first (FETCH_USERS) con fallback a GET_USER_BY_ID.
 * Devuelve '' si el id es nulo o el usuario no se encuentra.
 */
export async function resolveRegisteredByName(registeredByUserId?: string | null): Promise<string> {
  if (!registeredByUserId) return '';
  const cached = resolveUserFromCache(registeredByUserId);
  if (cached) {
    return `${cached.name ?? ''} ${cached.surName ?? ''}`.trim();
  }
  const data = await safeQuery<{ user: any }>(GET_USER_BY_ID, {
    input: { id: registeredByUserId },
  });
  return data?.user ? `${data.user.name ?? ''} ${data.user.surName ?? ''}`.trim() : '';
}

/**
 * Creador de la Order (`order.userId` → "Nom Cognom") como campo de la info
 * básica del informe. Mismo patrón que el Excel de históricos
 * (`gql/selectors/accidents.ts`): caché de FETCH_USERS primero, fallback a
 * GET_USER_BY_ID. Si no resuelve devuelve [] → el campo no se pinta (el
 * `DataGrid` ya omite los vacíos).
 */
export async function resolveCreatorFields(
  userId?: string | null
): Promise<{ label: string; value: string }[]> {
  if (!userId) return [];

  const cached = resolveUserFromCache(userId);
  const user =
    cached ??
    (await safeQuery<{ user: any }>(GET_USER_BY_ID, { input: { id: userId } }))
      ?.user;
  if (!user) return [];

  const name = `${user.name ?? ''} ${user.surName ?? ''}`.trim();
  if (!name) return [];

  return [{ label: t('common:labels.creator', 'Creador'), value: name }];
}

// ---------------------------------------------------------------------------
// Sub-entity fetchers (action)
// ---------------------------------------------------------------------------

async function fetchOperators(
  userIDs: string[]
): Promise<DetailedPDFRecord['operators']> {
  if (!userIDs?.length) return [];

  return chunkedParallel(userIDs, async (userId) => {
    // Try cache first
    const cached = resolveUserFromCache(userId);
    const resolveConcession = async (concessionId: string): Promise<string> => {
      const fromCache = resolveConcessionName(concessionId);
      if (fromCache) return fromCache;
      const cData = await safeQuery<{ concession: any }>(GET_CONCESSION_BY_ID, { id: concessionId });
      return cData?.concession?.name ?? '';
    };

    if (cached) {
      const concessionName = cached.concessionIDs?.[0]
        ? await resolveConcession(cached.concessionIDs[0])
        : '';
      return {
        name: cached.name ?? '',
        surname: cached.surName ?? '',
        concession: concessionName,
      };
    }

    const data = await safeQuery<{ user: any }>(GET_USER_BY_ID, {
      input: { id: userId },
    });
    if (!data?.user) return { name: '', surname: '', concession: '' };
    const user = data.user;
    const concessionName = user.concessionIDs?.[0]
      ? await resolveConcession(user.concessionIDs[0])
      : '';
    return {
      name: user.name ?? '',
      surname: user.surName ?? '',
      concession: concessionName,
    };
  });
}

async function fetchVehicles(
  vehicleOccupantIDs: string[]
): Promise<DetailedPDFRecord['vehicles']> {
  if (!vehicleOccupantIDs?.length) return [];

  return chunkedParallel(vehicleOccupantIDs, async (voId) => {
    const voData = await safeQuery<{ vehicleOccupant: any }>(
      GET_VEHICLE_OCCUPANT_BY_ID,
      { id: voId }
    );
    if (!voData?.vehicleOccupant) {
      return { plate: '', model: '', category: '', driver: '' };
    }
    const vo = voData.vehicleOccupant;

    // Fetch vehicle
    const vehicleId = vo.vehicleIDs?.[0];
    let plate = '';
    let model = '';
    let category = '';
    if (vehicleId) {
      const vData = await safeQuery<{ vehicle: any }>(GET_VEHICLE_BY_ID, {
        id: vehicleId,
      });
      if (vData?.vehicle) {
        plate = vData.vehicle.plate ?? '';
        model = vData.vehicle.model ?? '';
        category = vData.vehicle.category ?? '';
      }
    }

    // Fetch driver
    let driver = '';
    const driverId = vo.userDriverIDs?.[0];
    if (driverId) {
      const cached = resolveUserFromCache(driverId);
      if (cached) {
        driver = `${cached.name ?? ''} ${cached.surName ?? ''}`.trim();
      } else {
        const uData = await safeQuery<{ user: any }>(GET_USER_BY_ID, {
          input: { id: driverId },
        });
        if (uData?.user) {
          driver = `${uData.user.name ?? ''} ${uData.user.surName ?? ''}`.trim();
        }
      }
    }

    return { plate, model, category, driver };
  });
}

async function fetchMaterials(
  materialUnitIDs: string[]
): Promise<DetailedPDFRecord['materials']> {
  if (!materialUnitIDs?.length) return [];

  return chunkedParallel(materialUnitIDs, async (muId) => {
    const muData = await safeQuery<{ materialUnit: any }>(
      GET_MATERIAL_UNIT_BY_ID,
      { id: muId }
    );
    if (!muData?.materialUnit) {
      return { code: '', description: '', unitType: '', unit: '', quantity: '' };
    }
    const mu = muData.materialUnit;
    const quantity = mu.unit != null ? formatQuantity(mu.unit) : '';

    const materialId = mu.materialIDs?.[0];
    if (!materialId) {
      return { code: '', description: '', unitType: '', unit: '', quantity };
    }

    const mData = await safeQuery<{ material: any }>(GET_MATERIAL_BY_ID, {
      id: materialId,
    });
    if (!mData?.material) {
      return { code: '', description: '', unitType: '', unit: '', quantity };
    }
    const mat = mData.material;
    return {
      code: mat.materialId ?? '',
      description: mat.name ?? '',
      unitType: quantity,
      unit: mat.typeOfMeasurement ?? '',
      quantity,
    };
  });
}

async function fetchMachinery(
  auxMachineUnitIDs: string[]
): Promise<DetailedPDFRecord['machinery']> {
  if (!auxMachineUnitIDs?.length) return [];

  return chunkedParallel(auxMachineUnitIDs, async (amuId) => {
    const amuData = await safeQuery<{ auxMachineUnit: any }>(
      GET_AUX_MACHINE_UNIT_BY_ID,
      { id: amuId }
    );
    if (!amuData?.auxMachineUnit) {
      return { code: '', description: '', unitType: '', unit: '', quantity: '' };
    }
    const amu = amuData.auxMachineUnit;
    const quantity = amu.unit != null ? String(amu.unit) : '';

    const machineId = amu.auxMachineIDs?.[0];
    if (!machineId) {
      return { code: '', description: '', unitType: '', unit: '', quantity };
    }

    const amData = await safeQuery<{ auxMachine: any }>(
      GET_AUX_MACHINE_BY_ID,
      { id: machineId }
    );
    if (!amData?.auxMachine) {
      return { code: '', description: '', unitType: '', unit: '', quantity };
    }
    const machine = amData.auxMachine;
    return {
      code: machine.auxMachineId ?? '',
      description: machine.description ?? '',
      unitType: quantity,
      unit: machine.typeOfMeasurement ?? '',
      quantity,
    };
  });
}

async function fetchAnimals(
  animalUnitIDs: string[]
): Promise<DetailedPDFRecord['animals']> {
  if (!animalUnitIDs?.length) return [];

  return chunkedParallel(animalUnitIDs, async (auId) => {
    const auData = await safeQuery<{ animalUnit: any }>(
      GET_ANIMAL_UNIT_BY_ID,
      { id: auId }
    );
    if (!auData?.animalUnit) {
      return { code: '', species: '', status: '', quantity: '' };
    }
    const au = auData.animalUnit;
    const quantity = au.unit != null ? String(au.unit) : '';

    // Fetch animal name and code
    let species = '';
    let code = '';
    const animalId = au.animalIDs?.[0];
    if (animalId) {
      const aData = await safeQuery<{ animal: any }>(GET_ANIMAL_BY_ID, {
        id: animalId,
      });
      species = aData?.animal?.name ?? '';
      code = aData?.animal?.animalId ?? '';
    }

    // Fetch status
    let status = '';
    const statusId = au.animalStatusIDs?.[0];
    if (statusId) {
      const sData = await safeQuery<{ animalStatus: any }>(
        GET_ANIMAL_STATUS_BY_ID,
        { id: statusId }
      );
      status = sData?.animalStatus?.description ?? '';
    }

    return { code, species, status, quantity };
  });
}

async function fetchBudgetItems(
  actionBudgetItemIDs: string[]
): Promise<DetailedPDFRecord['budgetItems']> {
  if (!actionBudgetItemIDs?.length) return [];

  return chunkedParallel(actionBudgetItemIDs, async (abiId) => {
    const data = await safeQuery<{ actionBudgetItem: any }>(
      GET_ACTION_BUDGET_ITEM_BY_ID,
      { id: abiId }
    );
    if (!data?.actionBudgetItem) {
      return {
        code: '',
        description: '',
        category: '',
        unit: '',
        quantity: '',
        unitPrice: '',
        total: '',
      };
    }
    const abi = data.actionBudgetItem;
    const bi = abi.budgetItem;
    return {
      code: bi?.code ?? '',
      description: bi?.description ?? '',
      category: bi?.category?.name ?? bi?.budgetCategory?.name ?? '',
      unit: bi?.unit ?? '',
      quantity: abi.quantity != null ? String(abi.quantity) : '',
      unitPrice: bi?.unitPrice != null ? String(bi.unitPrice) : '',
      total: abi.totalAmount != null ? String(abi.totalAmount) : '',
    };
  });
}

// ---------------------------------------------------------------------------
// Activity, Element, Asset, External Companies fetchers (action)
// ---------------------------------------------------------------------------

async function resolveActivityName(activityIDs: string[]): Promise<string> {
  const id = activityIDs?.[0];
  if (!id) return '';
  const data = await safeQuery<{ activity: any }>(GET_ACTIVITY_BY_ID, { id });
  return data?.activity?.name ?? '';
}

async function resolveElementName(elementOrderIDs: string[]): Promise<string> {
  const id = elementOrderIDs?.[0];
  if (!id) return '';
  const data = await safeQuery<{ elementOrder: any }>(GET_ELEMENT_ORDER_BY_ID, { id });
  return data?.elementOrder?.name ?? '';
}

async function resolveAssetCode(assetIDs: string[]): Promise<string> {
  const id = assetIDs?.[0];
  if (!id) return '';
  const data = await safeQuery<{ asset: any }>(GET_ASSET_BY_ID, { id });
  return data?.asset?.code ?? '';
}

function resolveExternalCompanyFromCache(companyId: string): string | null {
  try {
    const data = client.readQuery({ query: FETCH_EXTERNAL_COMPANIES });
    const found = data?.externalCompanies?.find((c: any) => c.id === companyId);
    return found?.companyName ?? null;
  } catch {
    return null;
  }
}

async function fetchExternalCompanies(
  externalWorksIDs: string[]
): Promise<DetailedPDFRecord['externalCompanies']> {
  if (!externalWorksIDs?.length) return [];

  return chunkedParallel(externalWorksIDs, async (ewId) => {
    const data = await safeQuery<{ externalWork: any }>(GET_EXTERNAL_WORK_BY_ID, { id: ewId });
    if (!data?.externalWork) return { company: '', workers: '', notes: '' };
    const ew = data.externalWork;
    const companyId = ew.externalCompanyIDs?.[0];
    let companyName = '';
    if (companyId) {
      companyName = resolveExternalCompanyFromCache(companyId) ?? '';
      if (!companyName) {
        const compData = await safeQuery<{ externalCompany: any }>(GET_EXTERNAL_COMPANY_BY_ID, { id: companyId });
        companyName = compData?.externalCompany?.companyName ?? '';
      }
    }
    return {
      company: companyName,
      workers: ew.operatorsNumber != null ? String(ew.operatorsNumber) : '',
      notes: '',
    };
  });
}

async function fetchWorkVehicles(
  workVehicleIDs: string[]
): Promise<DetailedPDFRecord['workVehicles']> {
  if (!workVehicleIDs?.length) return [];

  return chunkedParallel(workVehicleIDs, async (vId) => {
    const vData = await safeQuery<{ vehicle: any }>(GET_VEHICLE_BY_ID, { id: vId });
    if (!vData?.vehicle) return { plate: '', model: '', category: '', driver: '', ownership: '' };
    const v = vData.vehicle;
    return {
      plate: v.plate ?? '',
      model: v.model ?? '',
      category: v.category ?? '',
      driver: '-',
      ownership: v.ownership ?? '',
    };
  });
}

// ---------------------------------------------------------------------------
// Image fetcher (shared by actions and accidents)
// ---------------------------------------------------------------------------

export async function fetchImages(
  imageIDs: string[],
  cloudName: string
): Promise<DetailedPDFRecord['images']> {
  if (!imageIDs?.length) return [];

  // Dónde se hizo cada foto, escrito bajo su nombre (spec fotos geolocalizadas
  // § 19): un PDF no conserva el EXIF. `null` sin el flag del tenant → ni una
  // lectura de más. Un contexto por llamada: las fotos de un mismo sitio
  // comparten la búsqueda del PK.
  const locationCtx = await createPhotoLocationContext(client, (key, options) =>
    i18n.t(key, options) as string,
  );

  return chunkedParallel(imageIDs, async (imgId) => {
    const data = await safeQuery<{ image: any }>(GET_IMAGE_BY_ID, {
      id: imgId,
    });
    if (!data?.image) return { url: '', name: '' };
    const img = data.image;
    // Backend-resolved URL (Hetzner→Cloudinary fetch firmado, o legacy upload);
    // resolveImageUrl applies the legacy cloudinaryPublicId fallback internally.
    // 'pdf' → tier PdfOptimized (cap 1024px, q_auto:low, JPEG baseline): igual de
    // ligera que 'thumb' pero fuerza `f_jpg,fl_progressive:none`. @react-pdf/renderer
    // descarta silenciosamente ciertos JPEG PROGRESIVOS que Cloudinary sirve con
    // `f_auto,q_auto:low` (síntoma: informe con 10 fotos válidas pintaba solo 7,
    // determinista). Baseline las hace todas decodificables sin penalizar tamaño.
    // Punto único compartido por detallado, histórico, croquis e informe combinado
    // (fetchCombinedReportData reutiliza este fetchImages).
    const url = resolveImageUrl(img, 'pdf') ?? '';
    const name = img.name ?? '';
    // Los croquis son dibujos: no tienen dónde se hicieron.
    const location =
      locationCtx && url && !isSketchName(name)
        ? await photoLocationLines(img, locationCtx)
        : undefined;
    return location ? { url, name, location } : { url, name };
  });
}

// ---------------------------------------------------------------------------
// Resolve basic info fields for location / classification
// ---------------------------------------------------------------------------

export async function resolveLocationFields(
  entity: any
): Promise<{ label: string; value: string }[]> {
  const fields: { label: string; value: string }[] = [];

  // Concession
  const concessionId = entity.concessionIDs?.[0];
  if (concessionId) {
    const name =
      resolveConcessionName(concessionId) ??
      (
        await safeQuery<{ concession: any }>(GET_CONCESSION_BY_ID, {
          id: concessionId,
        })
      )?.concession?.name ??
      '';
    fields.push({ label: t('tables:headers.concession'), value: name });
  }

  // Road (also fetch directions for direction display)
  const roadId = entity.roadIDs?.[0];
  let roadDirections: string[] = [];
  if (roadId) {
    // Try cache first for name
    let roadName = resolveRoadName(roadId);
    if (!roadName) {
      const roadData = await safeQuery<{ road: any }>(GET_ROAD_BY_ID, { id: roadId });
      roadName = roadData?.road?.name ?? '';
      roadDirections = roadData?.road?.directions ?? [];
    } else {
      // If name from cache, also try to get directions from GET_ROAD_BY_ID
      const roadData = await safeQuery<{ road: any }>(GET_ROAD_BY_ID, { id: roadId });
      roadDirections = roadData?.road?.directions ?? [];
    }
    if (roadName) fields.push({ label: t('forms:workOrder.road', 'Carretera'), value: roadName });
  }

  // PKs
  const pkInit = formatPk(entity.pkInitKm, entity.pkInitMeter);
  const pkEnd = formatPk(entity.pkEndKm, entity.pkEndMeter);
  if (pkInit) fields.push({ label: t('forms:workOrder.pkInitial', 'PK inicial'), value: pkInit });
  if (pkEnd) fields.push({ label: t('tables:headers.pkEnd'), value: pkEnd });

  // Direction — use road.directions for city names (arrows not supported by PDF font)
  const dirValue = entity.direction;
  if (dirValue && dirValue !== 'sense especificar') {
    let dirDisplay = '';
    if (dirValue === 'down' && roadDirections[0]) {
      dirDisplay = `${roadDirections[0]} (v)`;
    } else if (dirValue === 'top' && roadDirections.length > 0) {
      dirDisplay = `${roadDirections[roadDirections.length - 1]} (^)`;
    } else if (dirValue === 'both') {
      const names = roadDirections.filter(Boolean);
      dirDisplay = names.length >= 2 ? `${names[0]} / ${names[names.length - 1]}` : 'Ambdos';
    } else {
      dirDisplay = dirValue;
    }
    if (dirDisplay) fields.push({ label: t('forms:workOrder.direction', 'Sentit'), value: dirDisplay });
  }
  if (entity.lane) fields.push({ label: t('forms:workOrder.lane', 'Carril'), value: translateLane(entity.lane) });
  if (entity.margin && entity.margin !== 'sense especificar') {
    fields.push({ label: t('forms:workOrder.margin', 'Marge'), value: entity.margin });
  }
  if (entity.track) fields.push({ label: t('forms:workOrder.roadType'), value: translateTrack(entity.track) });

  return fields;
}

export async function resolveClassificationFields(
  entity: any
): Promise<{ label: string; value: string }[]> {
  const fields: { label: string; value: string }[] = [];

  // Capitol
  const capitolId = entity.capitolIDs?.[0];
  if (capitolId) {
    const name =
      resolveCapitolName(capitolId) ??
      (
        await safeQuery<{ capitol: any }>(GET_CAPITOL_BY_ID, {
          id: capitolId,
        })
      )?.capitol?.name ??
      '';
    fields.push({ label: t('forms:workOrder.chapter', 'Capítol'), value: name });
  }

  // SubCapitol
  const subCapitolId = entity.subCapitolIDs?.[0];
  if (subCapitolId) {
    const name =
      resolveSubCapitolName(subCapitolId) ??
      (
        await safeQuery<{ subCapitol: any }>(GET_SUB_CAPITOL_BY_ID, {
          id: subCapitolId,
        })
      )?.subCapitol?.name ??
      '';
    fields.push({ label: t('forms:workOrder.subchapter', 'Subcapítol'), value: name });
  }

  // Classificació
  const classificationId = entity.classificationIDs?.[0];
  if (classificationId) {
    const data = await safeQuery<{ classification: any }>(
      GET_CLASSIFICATION_BY_ID_OR_ROID,
      { id: classificationId }
    );
    const name = data?.classification?.name ?? '';
    if (name) {
      fields.push({
        label: t('forms:workOrder.classification', 'Classificació'),
        value: name,
      });
    }
  }

  // Predefinit — se resuelve contra la lista completa (cache-first: una sola
  // query para todo el informe, no una por registro). Mismo patrón que el Excel.
  const predefinedId = entity.predefinedOrderIDs?.[0];
  if (predefinedId) {
    const data = await safeQuery<{ predefinedOrders: any[] }>(
      FETCH_PREDEFINED_ORDERS,
      {}
    );
    const name =
      data?.predefinedOrders?.find((p: any) => p.id === predefinedId)?.name ??
      '';
    if (name) {
      fields.push({
        label: t('forms:workOrder.predefined', 'Predefinits'),
        value: name,
      });
    }
  }

  return fields;
}

// ---------------------------------------------------------------------------
// Fetch a single ACTION order record
// ---------------------------------------------------------------------------

export async function fetchActionRecord(
  orderId: string,
  cloudName: string
): Promise<DetailedPDFRecord | null> {
  // 1. Get parent order
  const orderData = await safeQuery<{ order: any }>(GET_ORDER_BY_ID, {
    id: orderId,
  });
  if (!orderData?.order) return null;
  const order = orderData.order;

  const roid = formatRoid(order.roid);
  const actionId = order.actionIDs?.[0];
  if (!actionId) return null;

  // 2. Get action
  const actionData = await safeQuery<{ action: any }>(GET_ACTION_BY_ID, {
    id: actionId,
  });
  if (!actionData?.action) return null;
  const action = actionData.action;

  // Corte de carril unificado (2026-07-29): resuelto una sola vez por hija;
  // el informe de OT llama a este builder una vez por cada actuación.
  const laneCutIds = await loadLaneCutIds();

  // 3. Resolve additional fields in parallel
  const [locationFields, classificationFields, creatorFields, activityName, elementName, assetCode] =
    await Promise.all([
      resolveLocationFields(action),
      resolveClassificationFields(order),
      resolveCreatorFields(order.userId),
      resolveActivityName(action.activityIDs ?? []),
      resolveElementName(order.elementOrderIDs ?? []),
      resolveAssetCode(action.assetIDs ?? []),
    ]);

  // Calculate total hours from minutesTotals
  const totalHours = action.minutesTotals != null
    ? `${Math.floor(action.minutesTotals / 60)}h ${action.minutesTotals % 60}min`
    : '';

  // Operario que registra (Order.registeredByUserId → nombre). Cache-first
  // (FETCH_USERS) con fallback a GET_USER_BY_ID, mismo mecanismo que el resto
  // de resoluciones de usuario de este fichero.
  const registeredByName = await resolveRegisteredByName(order.registeredByUserId);

  const basicInfo: { label: string; value: string }[] = [
    { label: 'ROID', value: roid },
    { label: t('tables:headers.status', 'Estat'), value: translateState(order.state ?? '') },
    ...(order.registerInitDate
      ? [{ label: t('forms:workOrder.startDate', 'Data registre'), value: formatDate(order.registerInitDate) }]
      : []),
    ...creatorFields,
    ...(order.registeredByUserId
      ? [{ label: t('forms:registeredBy.label', 'Registrat per'), value: registeredByName }]
      : []),
    ...classificationFields,
    ...locationFields,
  ];

  // Actuació bàsica (hija de un Recorregut de Vigilància): su traje reducido
  // decide qué secciones tiene el informe. Se resuelve por el DATO
  // (`containerType`), nunca por la pantalla desde la que se pide el PDF.
  const isBasic = isBasicAction({ containerType: order.containerType });

  // 4. Fetch sub-entities in parallel
  const [
    operators, vehicles, workVehicles, externalCompanies,
    materials, machinery, animals, budgetItems, images,
  ] = await Promise.all([
    // Tripulación (2026-09-04): la básica NO la tiene —es del recorrido—, así
    // que su informe no la lista. Antes solo se ocultaba por lista vacía, y
    // una básica antigua con tripulación heredada la imprimía como suya.
    isBasic ? Promise.resolve([]) : fetchOperators(action.userIDs ?? []),
    isBasic
      ? Promise.resolve([])
      : fetchVehicles(action.vehicleOccupantIDs ?? []),
    fetchWorkVehicles(action.workVehicleIDs ?? []),
    fetchExternalCompanies(action.externalWorksIDs ?? []),
    fetchMaterials(action.materialUnitIDs ?? []),
    fetchMachinery(action.auxMachineUnitIDs ?? []),
    // Fauna (2026-08-11): la básica no la registra, así que su informe no la
    // lista — y de paso se ahorra las dos consultas de catálogo que trae.
    isBasic ? Promise.resolve([]) : fetchAnimals(action.animalUnitIDs ?? []),
    // Roles ciegos a budgets (capataz) no piden partidas: backend → Forbidden
    roleCanViewBudgets(getCurrentRole())
      ? fetchBudgetItems(action.actionBudgetItemIDs ?? [])
      : Promise.resolve([]),
    fetchImages(order.imageIDs ?? [], cloudName),
  ]);

  // 5. Calculate H.P. breakdown (hours-person) — same logic as ActionContentCardResume
  const hoursBreakdown: { label: string; value: string; bold?: boolean }[] = [];
  const hp = t('forms:operatorsAndVehicles.personHours', 'H.P.');

  // formatMinutesToHHMM — same format as the app summary
  const fmtHHMM = (mins: number): string => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  // Jornada (rawShiftMinutes) con fechas COMPLETAS — MISMO criterio que el
  // resumen on-screen (ActionContentCardResume.fullShiftMinutes) y el wizard
  // (calculateHourDifference) y el backend (computeDurationMinutes):
  // `differenceInMinutes` con segundos+ms normalizados a 0 (cuenta solo HH:MM →
  // evita el bug "-1 min" de timestamps con segundos residuales) y soporta
  // jornadas CROSS-DÍA (antes, restando HH:mm sueltos con `endMin > startMin`,
  // una jornada 23:00→01:00 daba 0). Sin hora-fin válida (ausente o ≤ inicio) la
  // jornada es 0 y no se muestra desglose: NO se cae a minutesTotals (evita
  // inflar H.P.). "Total de horas" sigue mostrando el minutesTotals guardado por
  // separado. La hora de llegada no entra.
  const rawShiftMinutes = ((): number => {
    if (!order.registerInitDate || !order.registerEndDate) return 0;
    const s = new Date(order.registerInitDate);
    const e = new Date(order.registerEndDate);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
    s.setSeconds(0, 0);
    e.setSeconds(0, 0);
    const diff = differenceInMinutes(e, s);
    return Number.isNaN(diff) || diff <= 0 ? 0 : diff;
  })();

  // Actuación simultánea: la franja se registra pero NO cuenta horas — el
  // operario ya las cuenta en su trabajo principal (y en una Actuació bàsica
  // ese trabajo es el turno del Recorregut de Vigilància, cuya ventana ya
  // contiene este rato). El resumen en pantalla lo respetaba desde #44/#46;
  // el PDF NO, y sacaba «Total H.P.» con horas reales — contradiciendo a la
  // pantalla, a `minutesTotals` (que vale 0) y a Gestión Horaria. Afecta a
  // TODAS las simultáneas; la básica solo lo hacía sistemático.
  const isSimultaneousAction = action.isSimultaneous === true;

  // Actuació bàsica: la tripulación y las horas son del Recorregut de
  // Vigilància — el turno se mide con los TRAMOS y su informe ya lista quién
  // patrulló. Sacar aquí un desglose de «Horas por Persona» de una lista de
  // operarios vacía solo genera contradicción (la tabla de operarios ya no se
  // pinta, porque se oculta sola sin datos). Supera la decisión D6 del spec
  // del 2026-08-01, que lo conservaba asumiendo tripulación heredada.
  // Spec: docs/superpowers/specs/2026-08-05-actuacion-basica-sin-tripulacion-design.md
  // (`isBasic` se resuelve arriba, antes de las consultas de sub-entidades.)

  if (!isBasic && rawShiftMinutes > 0) {
    // Total shift hours — la JORNADA sí se muestra siempre (es la franja
    // trabajada); lo que no cuenta son las horas-persona derivadas.
    hoursBreakdown.push({
      label: t('forms:workOrder.totalHours', 'Total hores'),
      value: fmtHHMM(rawShiftMinutes),
    });

    const internalCount = operators?.length ?? 0;
    const internalMinutes = internalCount * rawShiftMinutes;

    // Internal operators H.P.
    if (internalCount > 0) {
      hoursBreakdown.push({
        label: `${t('forms:operatorsAndVehicles.internalOperatorsHours', 'Op. interns')} (${internalCount}) ${hp}`,
        value: fmtHHMM(internalMinutes),
      });
    }

    // External companies H.P. (per company)
    let totalExtMinutes = 0;
    const externalBreakdownItems: { company: string; count: number; minutes: number }[] = [];
    if (externalCompanies && externalCompanies.length > 0) {
      for (const ec of externalCompanies) {
        const count = parseInt(ec.workers) || 0;
        const mins = count * rawShiftMinutes;
        totalExtMinutes += mins;
        externalBreakdownItems.push({ company: ec.company, count, minutes: mins });
      }
      for (const ext of externalBreakdownItems) {
        if (ext.company) {
          hoursBreakdown.push({
            label: `${ext.company} (${ext.count} op.) ${hp}`,
            value: fmtHHMM(ext.minutes),
          });
        }
      }
    }

    // Subtotals only if both internal AND external exist (hasBothTypes)
    const hasBothTypes = internalCount > 0 && externalBreakdownItems.length > 0;
    if (hasBothTypes) {
      hoursBreakdown.push({
        label: t('forms:operatorsAndVehicles.totalInternalHours', 'Total H. internes'),
        value: fmtHHMM(internalMinutes),
      });
      hoursBreakdown.push({
        label: t('forms:operatorsAndVehicles.totalExternalHours', 'Total H. externes'),
        value: fmtHHMM(totalExtMinutes),
      });
    }

    // Grand total H.P. (always shown). Simultánea → 0, con la etiqueta que ya
    // usa el resumen en pantalla, para que se entienda que no es un error.
    const grandTotalMinutes = internalMinutes + totalExtMinutes;
    hoursBreakdown.push({
      label: isSimultaneousAction
        ? `${t('forms:operatorsAndVehicles.totalPersonHours', 'Total H.P.')} · ${t('forms:operatorsAndVehicles.simultaneous.label', 'Simultània')}`
        : t('forms:operatorsAndVehicles.totalPersonHours', 'Total H.P.'),
      value: fmtHHMM(isSimultaneousAction ? 0 : grandTotalMinutes),
      bold: true,
    });
  }

  // 6. Format budget items prices as currency (locale-aware)
  const currencyLocale = { ca: 'ca-ES', es: 'es-ES', en: 'en-GB' }[i18n.language] || 'es-ES';
  const fmtCurrency = (val: string) => {
    const num = parseFloat(val);
    if (isNaN(num)) return val;
    return num.toLocaleString(currencyLocale, { style: 'currency', currency: 'EUR' });
  };
  const formattedBudgetItems = budgetItems?.map(bi => ({
    ...bi,
    unitPrice: bi.unitPrice ? fmtCurrency(bi.unitPrice) : '',
    total: bi.total ? fmtCurrency(bi.total) : '',
  }));
  // Add TOTAL row
  const budgetTotal = budgetItems?.reduce((sum, bi) => sum + (parseFloat(bi.total) || 0), 0) ?? 0;
  if (budgetTotal > 0 && formattedBudgetItems) {
    formattedBudgetItems.push({
      code: '', description: '', category: '', unit: '', quantity: '',
      unitPrice: 'TOTAL',
      total: fmtCurrency(String(budgetTotal)),
    });
  }

  const actionImages = splitSketchImages(images);

  return {
    roid,
    type: 'action',
    state: order.state ?? '',
    basicInfo,
    // Una sola descripción: la validada o, si no hay, la del operario
    // (spec 2026-10-28). Nunca las dos.
    description: action.descriptionValidated || order.description || '',
    notes: order.notes ?? undefined,
    activity: activityName || undefined,
    element: elementName || undefined,
    asset: (action.assetSwitch && assetCode) ? assetCode : undefined,
    // Criterio unificado (2026-07-29): flag OR actividad OR OT predefinida.
    // Antes solo miraba el flag y omitía la fila entera cuando venía a null;
    // ahora siempre es calculable, así que siempre se pinta.
    laneCutting: resolveIsLaneCut(
      action.isLaneCut,
      {
        laneCutting: action.laneCutting,
        activityIDs: action.activityIDs,
        predefinedOrderIDs: action.predefinedOrderIDs,
      },
      laneCutIds,
    )
      ? t('forms:actionEdition.yes', 'Sí')
      : t('forms:actionEdition.no', 'No'),
    startDate: order.registerInitDate ? formatDate(order.registerInitDate) : undefined,
    endDate: order.registerEndDate ? formatDate(order.registerEndDate) : undefined,
    // Hora de llegada: exclusiva de Action (no está en Order). formatDate ya
    // incluye HH:mm. No participa en el cálculo de horas.
    arrivalDate: action.registerArrivalDate ? formatDate(action.registerArrivalDate) : undefined,
    totalHours: totalHours || undefined,
    totalOperators:
      !isBasic && action.userIDs?.length ? String(action.userIDs.length) : undefined,
    hoursBreakdown: hoursBreakdown.length > 0 ? hoursBreakdown : undefined,
    operators,
    vehicles,
    workVehicles: workVehicles?.length ? workVehicles : undefined,
    externalCompanies: externalCompanies?.length ? externalCompanies : undefined,
    materials,
    machinery,
    animals,
    budgetItems: formattedBudgetItems,
    sketches: actionImages.sketches,
    images: actionImages.photos,
  };
}

// ---------------------------------------------------------------------------
// Fetch a single ACCIDENT order record
// ---------------------------------------------------------------------------

export async function fetchAccidentRecord(
  orderId: string,
  cloudName: string
): Promise<DetailedPDFRecord | null> {
  // 1. Get parent order
  const orderData = await safeQuery<{ order: any }>(GET_ORDER_BY_ID, {
    id: orderId,
  });
  if (!orderData?.order) return null;
  const order = orderData.order;

  const roid = formatRoid(order.roid);
  const accidentId = order.accidentIDs?.[0];
  if (!accidentId) return null;

  // 2. Get accident
  const accidentData = await safeQuery<{ accident: any }>(
    GET_ACCIDENT_BY_ID,
    { id: accidentId }
  );
  if (!accidentData?.accident) return null;
  const accident = accidentData.accident;

  // 3. Build basic info
  const [locationFields, classificationFields, creatorFields] = await Promise.all([
    resolveLocationFields(accident),
    resolveClassificationFields(order),
    resolveCreatorFields(order.userId),
  ]);

  const yes = t('forms:actionEdition.yes', 'Sí');
  const no = t('forms:actionEdition.no', 'No');

  // Operario que registra (Order.registeredByUserId → nombre), mismo mecanismo
  // de resolución de usuario cache-first del resto del fichero.
  const registeredByName = await resolveRegisteredByName(order.registeredByUserId);

  // Corte de carril unificado (2026-07-29): resuelto una sola vez por hija;
  // el informe de OT llama a este builder una vez por cada accidente.
  const laneCutIds = await loadLaneCutIds();

  const basicInfo: { label: string; value: string }[] = [
    { label: 'ROID', value: roid },
    { label: t('tables:headers.status', 'Estat'), value: translateState(order.state ?? '') },
    ...(order.registerInitDate
      ? [{ label: t('forms:workOrder.startDate', 'Data registre'), value: formatDate(order.registerInitDate) }]
      : []),
    ...(order.registeredByUserId
      ? [{ label: t('forms:registeredBy.label', 'Registrat per'), value: registeredByName }]
      : []),
    // Criterio unificado: flag OR OT predefinida (Accident no tiene relación
    // con Activity). predefinedOrderIDs vive en la Order gemela, NO en
    // Accident — leerlo de `accident` daría undefined en silencio.
    {
      label: t('forms:accident.laneCutting', 'Tall de carril'),
      value: resolveIsLaneCut(
        accident.isLaneCut,
        {
          laneCutting: accident.laneCutting,
          predefinedOrderIDs: order.predefinedOrderIDs,
        },
        laneCutIds,
      )
        ? yes
        : no,
    },
    ...(accident.policeReport != null
      ? [{ label: t('forms:accident.policeReport', 'Atestat'), value: accident.policeReport ? yes : no }]
      : []),
    ...(accident.policeReportNumber
      ? [{ label: t('forms:accident.policeReportNumber', 'Núm. atestat'), value: accident.policeReportNumber }]
      : []),
    ...creatorFields,
    ...classificationFields,
    ...locationFields,
  ];

  // 4. Fetch conditions in parallel
  const conditionFetchers: Promise<{ label: string; value: string } | null>[] =
    [];

  if (accident.weatherIDs?.[0]) {
    conditionFetchers.push(
      safeQuery<{ weather: any }>(GET_WEATHER_BY_ID, {
        id: accident.weatherIDs[0],
      }).then((d) =>
        d?.weather ? { label: t('forms:accident.weather', 'Temps'), value: d.weather.name } : null
      )
    );
  }
  if (accident.luminosityIDs?.[0]) {
    conditionFetchers.push(
      safeQuery<{ luminosity: any }>(GET_LUMINOSITY_BY_ID, {
        id: accident.luminosityIDs[0],
      }).then((d) =>
        d?.luminosity
          ? { label: t('forms:accident.luminosity', 'Lluminositat'), value: d.luminosity.name }
          : null
      )
    );
  }
  if (accident.surfaceConditionIDs?.[0]) {
    conditionFetchers.push(
      safeQuery<{ surfaceCondition: any }>(GET_SURFACE_CONDITION_BY_ID, {
        id: accident.surfaceConditionIDs[0],
      }).then((d) =>
        d?.surfaceCondition
          ? { label: t('forms:accident.surfaceState', 'Estat superfície'), value: d.surfaceCondition.name }
          : null
      )
    );
  }
  if (accident.conditionsDrivingIDs?.[0]) {
    conditionFetchers.push(
      safeQuery<{ conditionsDriving: any }>(GET_CONDITIONS_DRIVING_BY_ID, {
        id: accident.conditionsDrivingIDs[0],
      }).then((d) =>
        d?.conditionsDriving
          ? { label: t('forms:accident.trafficConditions', 'Condicions conducció'), value: d.conditionsDriving.name }
          : null
      )
    );
  }
  if (accident.mediumKnowledgeIDs?.[0]) {
    conditionFetchers.push(
      safeQuery<{ mediumKnowledge: any }>(GET_MEDIUM_KNOWLEDGE_BY_ID, {
        id: accident.mediumKnowledgeIDs[0],
      }).then((d) =>
        d?.mediumKnowledge
          ? { label: t('forms:accident.mediumKnowledge', 'Mitjà coneixement'), value: d.mediumKnowledge.name }
          : null
      )
    );
  }
  if (accident.surveillanceBodyIDs?.[0]) {
    conditionFetchers.push(
      safeQuery<{ surveillanceBody: any }>(GET_SURVEILLANCE_BODY_BY_ID, {
        id: accident.surveillanceBodyIDs[0],
      }).then((d) =>
        d?.surveillanceBody
          ? { label: t('forms:accident.surveillanceBody', 'Cos vigilància'), value: d.surveillanceBody.name }
          : null
      )
    );
  }
  if (accident.roadConditionDescription) {
    conditionFetchers.push(
      Promise.resolve({
        label: t('forms:accident.incidentDescription', 'Descripció incidència'),
        value: accident.roadConditionDescription,
      })
    );
  }

  // 5. Fetch vehicle contexts for accident
  const vehicleContextsPromise = safeQuery<{
    vehicleContextsByAccident: any[];
  }>(GET_VEHICLE_CONTEXTS_BY_ACCIDENT, { accidentId });

  // 6. Fetch attention accident details
  const attentionPromise = accident.attentionAccidentIDs?.[0]
    ? safeQuery<{ attentionAccident: any }>(GET_ATTENTION_ACCIDENT_BY_ID, {
        id: accident.attentionAccidentIDs[0],
      })
    : Promise.resolve(null);

  // 7. Fetch images
  const imagesPromise = fetchImages(order.imageIDs ?? [], cloudName);

  const [conditionResults, vehicleContextsData, attentionData, images] =
    await Promise.all([
      Promise.all(conditionFetchers),
      vehicleContextsPromise,
      attentionPromise,
      imagesPromise,
    ]);

  const conditions = conditionResults.filter(
    (c): c is { label: string; value: string } => c !== null
  );

  // Vehículos implicados: letra A/A1/B… + color. Mapeo puro y testeado en
  // `src/utils/pdf/vehiclesInvolved.ts` (tests: specs/accidents/pdf-accident-report).
  const vehiclesInvolved = buildVehiclesInvolved(
    vehicleContextsData?.vehicleContextsByAccident ?? []
  );

  // Build attention details
  let attentionDetails: DetailedPDFRecord['attentionDetails'];
  if (attentionData?.attentionAccident) {
    const att = attentionData.attentionAccident;
    const detailEntries: { label: string; value: string }[] = [];
    // Use i18n keys from accidents namespace for team names and header labels
    const avis = t('accidents:headers.avis', 'Avís');
    const arribada = t('accidents:headers.arribada', 'Arribada');
    const sortida = t('accidents:headers.sortida', 'Sortida');
    const roadTeam = t('accidents:teams.roadTeam', 'Equip vialitat');
    const squadCops = t('accidents:teams.squadCops', 'Mossos d\'Esquadra');
    const assistance = t('accidents:teams.assistance', 'Ambulància / Grua');
    const others = t('accidents:teams.others', 'Altres');

    const timeFields: [string, string][] = [
      ['noticeRoadTeamDate', `${avis} - ${roadTeam}`],
      ['arrivalRoadTeamDate', `${arribada} - ${roadTeam}`],
      ['departureRoadTeamDate', `${sortida} - ${roadTeam}`],
      ['noticeSquadBoysDate', `${avis} - ${squadCops}`],
      ['arrivalSquadBoysDate', `${arribada} - ${squadCops}`],
      ['departureSquadBoysDate', `${sortida} - ${squadCops}`],
      ['noticeAmbulanceCraneAssistanceDate', `${avis} - ${assistance}`],
      ['arrivalAmbulanceCraneAssistanceDate', `${arribada} - ${assistance}`],
      ['departureAmbulanceCraneAssistanceDate', `${sortida} - ${assistance}`],
      ['noticeOtherDate', `${avis} - ${others}`],
      ['arrivalOtherDate', `${arribada} - ${others}`],
      ['departureOtherDate', `${sortida} - ${others}`],
    ];
    for (const [field, label] of timeFields) {
      if (att[field]) {
        detailEntries.push({ label, value: formatDate(att[field]) });
      }
    }
    if (att.otherName) {
      detailEntries.push({ label: others, value: att.otherName });
    }
    if (detailEntries.length > 0) {
      attentionDetails = detailEntries;
    }
  }

  // El accidente también genera croquis (`canvas-accident`): va a su propia
  // sección, no mezclado con las fotos (igual que la actuación).
  const accidentImages = splitSketchImages(images);

  return {
    roid,
    type: 'accident',
    state: order.state ?? '',
    basicInfo,
    description: order.description ?? '',
    notes: order.notes ?? undefined,
    conditions,
    vehiclesInvolved,
    damages: accident.damages ?? undefined,
    attentionDetails,
    sketches: accidentImages.sketches,
    images: accidentImages.photos,
  };
}

// ---------------------------------------------------------------------------
// Communication record (sin imágenes por requisito de negocio)
// ---------------------------------------------------------------------------

export async function fetchCommunicationRecord(
  orderId: string
): Promise<DetailedPDFRecord | null> {
  // 1. Order hija (tipo communication)
  const orderData = await safeQuery<{ order: any }>(GET_ORDER_BY_ID, {
    id: orderId,
  });
  if (!orderData?.order) return null;
  const order = orderData.order;

  const roid = formatRoid(order.roid);
  const communicationId = order.communicationIDs?.[0];
  if (!communicationId) return null;

  // 2. Entidad Communication
  const commData = await safeQuery<{ communication: any }>(
    GET_COMMUNICATION_BY_ID,
    { id: communicationId }
  );
  if (!commData?.communication) return null;
  const comm = commData.communication;

  // 3. Resolver nombres en paralelo (ubicación desde la comunicación,
  //    clasificación desde la Order, igual que action/accident)
  const [locationFields, classificationFields, creatorFields, interlocutorName, mediaName] =
    await Promise.all([
      resolveLocationFields(comm),
      resolveClassificationFields(order),
      resolveCreatorFields(order.userId),
      (async () => {
        const id = comm.interlocutorGroupIDs?.[0];
        if (!id) return '';
        const d = await safeQuery<{ interlocutorGroup: any }>(
          GET_INTERLOCUTOR_GROUP_BY_ID,
          { id }
        );
        return d?.interlocutorGroup?.name ?? '';
      })(),
      (async () => {
        const id = comm.mediaOfCommunicationIDs?.[0];
        if (!id) return '';
        const d = await safeQuery<{ mediaOfCommunication: any }>(
          GET_MEDIA_OF_COMMUNICATION_BY_ID,
          { id }
        );
        return d?.mediaOfCommunication?.name ?? '';
      })(),
    ]);

  const basicInfo: { label: string; value: string }[] = [
    { label: 'ROID', value: roid },
    {
      label: t('tables:headers.status', 'Estat'),
      value: translateState(order.state ?? ''),
    },
    ...(order.registerInitDate
      ? [
          {
            label: t('forms:workOrder.startDate', 'Data registre'),
            value: formatDate(order.registerInitDate),
          },
        ]
      : []),
    ...(comm.sendStatus
      ? [
          {
            label: t('forms:communication.sendStatus', "Estat d'enviament"),
            value: String(comm.sendStatus),
          },
        ]
      : []),
    ...(interlocutorName
      ? [
          {
            label: t('forms:communication.interlocutorGroup', 'Grup interlocutor'),
            value: interlocutorName,
          },
        ]
      : []),
    ...(mediaName
      ? [
          {
            label: t('forms:communication.media', 'Mitjà'),
            value: mediaName,
          },
        ]
      : []),
    ...creatorFields,
    ...classificationFields,
    ...locationFields,
  ];

  return {
    roid,
    type: 'communication',
    state: order.state ?? '',
    basicInfo,
    description: order.description ?? '',
    notes: order.notes ?? undefined,
    images: [], // comunicaciones sin fotos por requisito de negocio
  };
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export async function fetchDetailedReportData(params: {
  orderIds: string[];
  type: 'Action' | 'Accident';
  cloudName: string;
  onProgress?: (current: number, total: number) => void;
}): Promise<DetailedPDFRecord[]> {
  const { orderIds, type, cloudName, onProgress } = params;
  const total = orderIds.length;
  const records: DetailedPDFRecord[] = [];
  let completed = 0;

  const fetchFn =
    type === 'Action' ? fetchActionRecord : fetchAccidentRecord;

  // Process in chunks of CHUNK_SIZE to avoid overloading
  for (let i = 0; i < orderIds.length; i += CHUNK_SIZE) {
    const chunk = orderIds.slice(i, i + CHUNK_SIZE);
    const chunkResults = await Promise.all(
      chunk.map(async (orderId) => {
        const record = await fetchFn(orderId, cloudName);
        completed++;
        onProgress?.(completed, total);
        return record;
      })
    );
    for (const record of chunkResults) {
      if (record) records.push(record);
    }
  }

  return records;
}
